import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import { typeLabel } from '@/utils/movie';

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 5500);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const go = (i: number) => setIndex((i + count) % count);
  const current = items[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] lg:h-[680px] overflow-hidden bg-primary-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.banner || item.cover}
            alt={item.title}
            title={`${item.title} - 牛牛视频`}
            className="w-full h-full object-cover object-center"
          />
        </div>
      ))}

      {/* 渐变遮罩，保证文字可读 */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40" />

      {/* 内容 */}
      <div className="relative h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl pt-16 md:pt-10 animate-fade-up" key={current.id}>
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-accent-950 text-xs font-bold">
              精选推荐
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs font-medium backdrop-blur-sm">
              {typeLabel(current.type)} · {current.category}
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-background-50 leading-tight">
            {current.title}
          </h2>
          <p className="mt-4 text-sm md:text-base text-background-100/90 leading-relaxed line-clamp-3 max-w-xl">
            {current.summary}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs md:text-sm text-background-200">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-400" />
              {current.rating.toFixed(1)} 分
            </span>
            <span>{current.year}</span>
            <span>{current.region}</span>
            <span>{current.status}</span>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-accent-950 text-sm font-bold whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/10 hover:bg-background-50/20 border border-background-50/40 text-background-50 text-sm font-medium whitespace-nowrap backdrop-blur-sm transition-colors cursor-pointer"
            >
              进入{typeLabel(current.type)}频道
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>

      {/* 指示点 */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 md:left-auto md:right-8 md:translate-x-0 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到${item.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>

      {/* 左右切换 */}
      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 border border-background-50/30 text-background-50 backdrop-blur-sm transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 border border-background-50/30 text-background-50 backdrop-blur-sm transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>
    </section>
  );
}